import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Modal, ModalBody, ModalFooter, ModalHeader, Spinner} from "reactstrap";
import ButtonIcon from "../../atoms/button/buttonIcon";
import Selection from "../selection/selection";
import {useStores} from "../../../stores/useStores";
import {ObservableList} from "../../../stores/observables/observableList";
import {KeyValue} from "../../../stores/observables/keyValue";
import CircleIcon from "../../atoms/circleIcon/circleIcon";
import {IconSource} from "../../../shared/enums/iconSource";
import NavigationButton from "../../atoms/button/navigationButton";
import CsvDownloader from "react-csv-downloader";

import {
  ProcessedBookingsFeatureName
} from "../../../shared/constants";
import {ToastMessageStatus} from "../../atoms/toastMessage/toastMessage";
import HeaderColor from "../../atoms/headerColor/headerColor";
import HaloIcon from "../../molecules/haloIcon/haloIcon";
import {useVillageFeatureToggle} from "../../../utils/featureToggle/villageFeatureToggle";
import EditField from "../../molecules/editField/editField";
import CheckBox from "../../atoms/checkBox/checkBox";
import "./eventPrintableCalendarModal.sass";
import {RadioGroupKeyValue} from "../../../stores/observables/radioGroupKeyValues/radioGroupKeyValue";
import RadioGroupEdit from "../../molecules/radioGroupEdit/radioGroupEdit";
import {SimpleRadioGroupKeyValue} from "../../../stores/observables/radioGroupKeyValues/simpleRadioGroupKeyValue";
import {ExportFormatType} from "../../../shared/enums/exportFormatType";
import {observer} from "mobx-react-lite";
import {eventCsvExporterConfigs} from "../../../shared/eventCsvExporterConfigs";
import {endOfMonth, startOfMonth} from "date-fns";
import EventSummaryItem from "../../../stores/domainStore/pages/models/eventSummaryItem";

interface IEventPrintableCalendarModal {
  isOpen: boolean;
  toggle: () => void;
  exportCalendarDates: ObservableList<KeyValue>;
}

const EventPrintableCalendarModal: React.FC<IEventPrintableCalendarModal> = ({
  isOpen,
  toggle,
  exportCalendarDates
}) => {
  const {
    uiStore: {selectableVillages, toastMessageStore, eventsUiStore},
    domainStore: {printableCalendarDomainStore, eventsDomainStore}
  } = useStores();

  const isEnhanced = eventsUiStore.isEnhancedCalendar;

  const [exportActivitiesProgramme, setExportActivitiesProgramme] =
    useState(true);
  const [exportFeaturedEvents, setExportFeaturedEvents] = useState(true);
  const [exportCancelPostponedEvents, setExportCancelPostponedEvents] =
    useState(false);
  const [sectionDisabled, setSectionDisabled] = useState(false);

  useEffect(() => {
    // To reset some selections when the modal is closed
    if (!isOpen) {
      setExportActivitiesProgramme(true);
      setExportFeaturedEvents(true);
      setExportCancelPostponedEvents(false);
      setSectionDisabled(false);
    }
  }, [isOpen]);

  const isDisabled =
    !exportCalendarDates.selectedItem ||
    (!exportActivitiesProgramme &&
      !exportFeaturedEvents &&
      !exportCancelPostponedEvents);

  const isPayByFeeEnabled = useVillageFeatureToggle(
    ProcessedBookingsFeatureName
  ).hasFeature.value;

  const {t} = useTranslation(["translation", "event"]);

  const [isExporting, setIsExporting] = useState(false);
  const villageName = selectableVillages.selectedItem?.label;
  const month = exportCalendarDates.selectedItem?.label;
  const exportCalendar = async (pdfTitle: string, section?: string) => {
    await printableCalendarDomainStore.downloadPrintableCalender(
      pdfTitle,
      exportCalendarDates.selectedItem!.value,
      selectableVillages.selectedItem!.value,
      eventsUiStore.selectedCalendarName,
      section
    );
  };

  const exportCalendarPDF = async () => {
    setIsExporting(true);

    try {
      if (isEnhanced && exportActivitiesProgramme) {
        const pdfTitle = `${villageName} - Activities Programme - ${month}.pdf`;
        await exportCalendar(pdfTitle, "ActivitiesProgramme");
      }

      if (isEnhanced && exportFeaturedEvents) {
        const pdfTitle = `${villageName} - Featured Events - ${month}.pdf`;
        await exportCalendar(pdfTitle, "FeaturedEvents");
      }

      if (isEnhanced && exportCancelPostponedEvents) {
        const pdfTitle = `${villageName} - Cancelled and Postponed Events - ${month}.pdf`;
        await exportCalendar(pdfTitle, "CancelledPostponedEvents");
      }

      if (!isEnhanced) {
        const pdfTitle = `${villageName} - ${eventsUiStore.selectedCalendarName} - Events Calendar - ${month}.pdf`;
        await exportCalendar(pdfTitle);
      }
    } catch {
      toastMessageStore.displayToastMessage(
        t("event:errors.exportCalendarError"),
        ToastMessageStatus.Danger
      );
    }

    setIsExporting(false);
  };

  const calendarColor = eventsUiStore.calendarColor;

  const exportFormatRadioGroupItems: ObservableList<RadioGroupKeyValue> =
    new ObservableList<RadioGroupKeyValue>(
      [
        new SimpleRadioGroupKeyValue(
          ExportFormatType.printablePDF,
          t("event:labels.printablePDF")
        ),
        new SimpleRadioGroupKeyValue(
          ExportFormatType.calendarSpreadsheet,
          t("event:labels.calendarSpreadsheet")
        )
      ],
      sectionDisabled
        ? ExportFormatType.calendarSpreadsheet
        : ExportFormatType.printablePDF //default or selected value in modal
    );

  let section = sectionDisabled ? "section-disabled" : "section-enabled";
  const getCsvExportFileName = () => {
    return isEnhanced
      ? `${villageName} - Activities Programme - ${month}`
      : `${villageName} - ${eventsUiStore.selectedCalendarName} - Events Calendar - ${month}`;
  };

  const getExportEvents = async () => {
    setIsExporting(true);
    const data = await eventsDomainStore.getEventsForExport(
      startOfMonth(new Date(exportCalendarDates.selectedItem!.value)),
      endOfMonth(new Date(exportCalendarDates.selectedItem!.value)),
      eventsUiStore.selectedCalendarName,
      true
    );

    setIsExporting(false);
    return Promise.resolve(
      data.map((e: EventSummaryItem) =>
        eventCsvExporterConfigs.mapEventToExportRow(
          e,
          t,
          isEnhanced,
          isPayByFeeEnabled
        )
      )
    );
  };

  const exportLabel = isExporting ? (
    <Spinner className="export-spinner" />
  ) : (
    t("translation:actions.export")
  );

  const onError = () => {
    setIsExporting(false);
    toastMessageStore.displayToastMessage(
      t("event:errors.downloadFailed"),
      ToastMessageStatus.Danger
    );
  };

  return (
    <Modal
      className={"event-printable-calendar-modal"}
      isOpen={isOpen}
      toggle={toggle}
      centered
    >
      {calendarColor && <HeaderColor color={calendarColor} />}
      <ModalHeader
        toggle={toggle}
        close={<NavigationButton icon={IconSource.exit} onClick={toggle} />}
      >
        {calendarColor && (
          <HaloIcon
            iconSource={eventsUiStore.calendarIcon}
            haloColor={calendarColor}
          />
        )}
        {t("event:titles.exportCalendar")}
      </ModalHeader>
      <ModalBody>
        <>
          <div className="village">
            <CircleIcon icon={IconSource.home} />
            <div>
              <span>
                {t("event:labels.retirementVillage", {
                  name: `${selectableVillages?.selectedItem?.label}`
                })}
              </span>
              <span className="type">{eventsUiStore.selectedCalendarName}</span>
            </div>
          </div>
          <Selection
            value={exportCalendarDates}
            label={t("event:labels.date")}
            placeholder={t("event:placeholders.selectMonth")}
            isDisabled={isExporting}
          />
          <div className="format">
            <RadioGroupEdit
              isDisabled={isExporting}
              items={exportFormatRadioGroupItems}
              label={t("event:labels.format")}
              name="ends"
              onSelect={() => {
                if (
                  exportFormatRadioGroupItems.selectedItem?.value ===
                  ExportFormatType.calendarSpreadsheet
                ) {
                  setSectionDisabled(true);
                  setExportActivitiesProgramme(true);
                  setExportFeaturedEvents(false);
                  setExportCancelPostponedEvents(false);
                } else {
                  setSectionDisabled(false);
                }
              }}
            />
          </div>
          <div className="form-separator" />
          <div className={section}>
            {isEnhanced && (
              <EditField label={t("event:labels:section")} isRequired={true}>
                <div className="section-options">
                  <CheckBox
                    value={exportActivitiesProgramme}
                    testId={"exportActivitiesProgramme"}
                    label={t("event:labels:activitiesProgramme")}
                    disabled={isExporting || sectionDisabled}
                    onValueChange={setExportActivitiesProgramme}
                  />
                  <CheckBox
                    value={exportFeaturedEvents}
                    testId={"exportFeaturedEvents"}
                    label={t("event:labels:featuredEvents")}
                    disabled={isExporting || sectionDisabled}
                    onValueChange={setExportFeaturedEvents}
                  />
                  <CheckBox
                    value={exportCancelPostponedEvents}
                    testId={"cancelPostponedEvents"}
                    label={t("event:labels:cancelPostponements")}
                    disabled={isExporting || sectionDisabled}
                    onValueChange={setExportCancelPostponedEvents}
                  />
                </div>
              </EditField>
            )}
          </div>
        </>
      </ModalBody>
      <ModalFooter>
        <div>
          {isExporting && (
            <div className="export-event-message">
              {t("event:messages.exportingMessage")}
            </div>
          )}
        </div>
        <div>
          {exportFormatRadioGroupItems.selectedItem?.value ===
          ExportFormatType.printablePDF ? (
            <ButtonIcon
              type="primary"
              icon={isExporting ? undefined : IconSource.export}
              onClick={exportCalendarPDF}
              isDisabled={isDisabled || isExporting}
            >
              {exportLabel}
            </ButtonIcon>
          ) : (
            <CsvDownloader
              filename={getCsvExportFileName()}
              extension={eventCsvExporterConfigs.fileExtension}
              wrapColumnChar={eventCsvExporterConfigs.wrapColumnChar}
              columns={eventCsvExporterConfigs.getColumns(
                t,
                isEnhanced,
                isPayByFeeEnabled
              )}
              datas={getExportEvents}
              handleError={onError}
            >
              <ButtonIcon
                type="primary"
                icon={IconSource.export}
                isDisabled={isDisabled || isExporting}
              >
                {exportLabel}
              </ButtonIcon>
            </CsvDownloader>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default observer(EventPrintableCalendarModal);
